import React, { useState, useEffect, useRef } from 'react';
import { IoLocationOutline, IoAlertCircleOutline, IoNavigateOutline } from 'react-icons/io5';
import SelectModal from './SelectModal';
import { checkAttendance } from '../../api/MemberMeetingApi';
import { calculateDistance } from '../../utils/locationUtils';
import { AlertModal } from './AlertModal';

const CheckModal = ({ isVisible, onClose, onComplete, meetingId, placeLatitude, placeLongitude }) => {
    const mapRef = useRef(null);
    const [currentPosition, setCurrentPosition] = useState(null);
    const [distanceToPlace, setDistanceToPlace] = useState(null);
    const [isWithinRange, setIsWithinRange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLocation, setIsLoadingLocation] = useState(true);
    const [locationError, setLocationError] = useState(null);
    const [map, setMap] = useState(null);
    const userMarkerRef = useRef(null);
    const placeMarkerRef = useRef(null);
    const circleRef = useRef(null);
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

    const MAX_DISTANCE_METERS = 1000; // 1km

    useEffect(() => {
        if (isVisible) {
            getCurrentLocation();
        }
    }, [isVisible]);

    useEffect(() => {
        if (isVisible && window.kakao && window.kakao.maps && placeLatitude && placeLongitude) {
            initializeMap();
        }
    }, [isVisible, placeLatitude, placeLongitude]);

    useEffect(() => {
        if (currentPosition && placeLatitude && placeLongitude) {
            const distance = calculateDistance(
                currentPosition.latitude,
                currentPosition.longitude,
                placeLatitude,
                placeLongitude
            );
            setDistanceToPlace(distance);
            setIsWithinRange(distance <= MAX_DISTANCE_METERS);
            updateMapMarkers();
        }
    }, [currentPosition, placeLatitude, placeLongitude]);

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const getCurrentLocation = async () => {
        setIsLoadingLocation(true);
        setLocationError(null);

        // 1. Flutter WebView 환경 체크
        if (window.flutter_inappwebview?.callHandler) {
            console.log('📱 Flutter 환경에서 위치 요청');
            try {
                const result = await window.flutter_inappwebview.callHandler('getLocation');
                console.log('📍 Flutter에서 받은 위치 응답:', result);
                if (result.success) {
                    setCurrentPosition({ latitude: result.latitude, longitude: result.longitude });
                } else {
                    console.warn('❌ Flutter 위치 실패:', result.error);
                    setLocationError('위치 정보를 가져올 수 없습니다. GPS를 활성화해주세요.');
                }
            } catch (error) {
                console.error('❌ Flutter 위치 요청 오류:', error);
                setLocationError('앱에서 위치 정보를 가져오는 중 오류가 발생했습니다.');
            } finally {
                setIsLoadingLocation(false);
            }
            return;
        }

        // 2. 일반 웹 브라우저 환경
        console.log('🌐 브라우저에서 위치 요청');
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentPosition({ latitude, longitude });
                    setIsLoadingLocation(false);
                },
                (error) => {
                    console.error('위치 정보를 가져올 수 없습니다:', error);
                    setLocationError('위치 정보를 가져올 수 없습니다. GPS를 활성화해주세요.');
                    setIsLoadingLocation(false);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000
                }
            );
        } else {
            setLocationError('위치 정보가 지원되지 않는 브라우저입니다.');
            setIsLoadingLocation(false);
        }
    };

    const initializeMap = () => {
        if (!mapRef.current || !window.kakao || !window.kakao.maps) return;

        const kakao = window.kakao;
        const center = new kakao.maps.LatLng(placeLatitude, placeLongitude);
        const mapOption = { center: center, level: 4 };
        const mapInstance = new kakao.maps.Map(mapRef.current, mapOption);
        setMap(mapInstance);

        placeMarkerRef.current = new kakao.maps.Marker({ position: center, map: mapInstance });
        circleRef.current = new kakao.maps.Circle({
            center: center,
            radius: MAX_DISTANCE_METERS,
            strokeWeight: 2,
            strokeColor: '#0000FF',
            strokeOpacity: 0.8,
            strokeStyle: 'solid',
            fillColor: '#0000FF',
            fillOpacity: 0.1
        });
        circleRef.current.setMap(mapInstance);
    };

    const updateMapMarkers = () => {
        if (!map || !currentPosition || !window.kakao || !window.kakao.maps) return;

        const kakao = window.kakao;
        if (userMarkerRef.current) {
            userMarkerRef.current.setMap(null);
        }

        const userPosition = new kakao.maps.LatLng(currentPosition.latitude, currentPosition.longitude);
        userMarkerRef.current = new kakao.maps.Marker({
            position: userPosition,
            map: map,
            image: new kakao.maps.MarkerImage(
                'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png',
                new kakao.maps.Size(24, 35)
            )
        });

        const bounds = new kakao.maps.LatLngBounds();
        bounds.extend(userPosition);
        bounds.extend(new kakao.maps.LatLng(placeLatitude, placeLongitude));
        map.setBounds(bounds);
    };

    const handleCheckAttendance = async () => {
        if (!currentPosition || !isWithinRange) return;

        try {
            setIsLoading(true);
            await checkAttendance(meetingId, currentPosition.latitude, currentPosition.longitude);
            setAlertState({
                isVisible: true,
                message: '출석체크가 완료되었습니다. 쌀 500알이 환급됩니다.',
                callback: onComplete || onClose
            });
        } catch (error) {
            console.error('출석체크 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    const formatDistance = (distance) => {
        if (distance === null) return '';
        if (distance < 1000) {
            return `${Math.round(distance)}m`;
        } else {
            return `${(distance / 1000).toFixed(1)}km`;
        }
    };

    return (
        <SelectModal
            isVisible={isVisible}
            onClose={onClose}
            title="출석 체크"
            onConfirm={handleCheckAttendance}
            confirmText="출석 체크"
            confirmDisabled={!isWithinRange || isLoadingLocation || !!locationError}
            isLoading={isLoading}
        >
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            {/* 상태 메시지 */}
            {isLoadingLocation ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                    <span className="text-gray-700 text-sm">로딩 중...</span>
                </div>
            ) : !locationError && distanceToPlace !== null && (
                <div className={`${
                    isWithinRange
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-red-50 border border-red-200'
                } rounded-lg p-3 mb-4 flex items-center space-x-2`}>
                    <IoAlertCircleOutline className={`${
                        isWithinRange ? 'text-blue-500' : 'text-red-500'
                    }`} size={20} />
                    <span className={`text-sm ${
                        isWithinRange ? 'text-blue-700' : 'text-red-700'
                    }`}>
                        {isWithinRange ? '출석이 가능합니다' : '모임 장소에서 출석체크해주세요'}
                    </span>
                </div>
            )}

            {/* 위치 오류 메시지 */}
            {locationError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                        <IoAlertCircleOutline className="text-red-500" size={20} />
                        <span className="text-red-700 text-sm font-medium">위치 오류</span>
                    </div>
                    <p className="text-red-600 text-sm">{locationError}</p>
                    <button
                        onClick={getCurrentLocation}
                        className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded active:bg-red-600"
                    >
                        다시 시도
                    </button>
                </div>
            )}

            {/* 지도 */}
            <div className="mb-4 relative">
                <div
                    ref={mapRef}
                    className="w-full h-64 rounded-lg border border-gray-300"
                />
                <div className="absolute top-2 right-2 z-10">
                    <button
                        onClick={getCurrentLocation}
                        className="p-2 bg-white border border-gray-300 rounded-full shadow-sm active:bg-gray-50 transition-colors"
                        title="현재 위치로 재설정"
                    >
                        <IoNavigateOutline size={18} className="text-gray-600" />
                    </button>
                </div>
            </div>

            {/* 상태 정보 */}
            <div className="space-y-3">
                {isLoadingLocation && (
                    <div className="flex items-center space-x-2 text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        <span className="text-sm">현재 위치를 확인 중...</span>
                    </div>
                )}
                {distanceToPlace !== null && !isLoadingLocation && (
                    <div className={`flex items-center space-x-2 ${isWithinRange ? 'text-green-600' : 'text-red-600'}`}>
                        <IoLocationOutline size={16} />
                        <span className="text-sm">
                            모임 장소까지 {formatDistance(distanceToPlace)}
                            {isWithinRange && ' (출석 가능)'}
                        </span>
                    </div>
                )}
                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    모임 장소에서 1km 이내에 있어야 출석체크가 가능합니다.
                </div>
            </div>
        </SelectModal>
    );
};

export default CheckModal;
